import { Controller } from "@hotwired/stimulus"
import { useDatasetFilters } from "./mixins/use_dataset_filters"

const STORAGE_PREFIX = 'table-filter'

export default class extends Controller {
  static outlets = ['table-filter-render']
  static values  = {
    comparisonType: String,
    comparisonSelector: String,
    comparisonFindPath: String,
    storageKey: String,
  }

  identifier = crypto.randomUUID()

  initialize() {
    useDatasetFilters(this)
    this.restored = false
  }

  // Restoring needs the table we render into, and outlets are not guaranteed to be
  // wired up during connect(), so wait until the outlet announces itself.
  tableFilterRenderOutletConnected() {
    if (this.restored) { return }

    this.restored = true
    this.restore()
  }

  filter() {
    document
      .querySelectorAll(this.comparisonSelectorValue)
      .forEach(this.compare.bind(this))

    this.tableFilterRenderOutlet.render()
    this.persist()
  }

  // Filters live only in the DOM, so any full page load — clicking into a row and back,
  // the sidebar link, a redirect after save — would otherwise reset them.
  persist() {
    if (!this.hasStorageKeyValue) { return }

    if (this.element.value) {
      window.sessionStorage.setItem(this.storageId, this.element.value)
    } else {
      window.sessionStorage.removeItem(this.storageId)
    }
  }

  restore() {
    if (!this.hasStorageKeyValue) { return }

    // A value rendered by the server (a shared link carrying filter params) is an
    // explicit request, so it wins over whatever was stored earlier.
    if (this.element.value) { return this.filter() }

    const value = window.sessionStorage.getItem(this.storageId)
    if (!value || !this.selectable(value)) { return }

    this.applyValue(value)
    this.filter()
  }

  // A stored option may be gone — a category belonging to a storefront the user has
  // since switched away from — and restoring it would silently empty the table.
  selectable(value) {
    if (this.element.tagName !== 'SELECT') { return true }

    return [...this.element.options].some(option => option.value === value)
  }

  // Tom Select owns the visible control, so writing to the underlying select alone
  // would apply the filter without ever showing it. Setting it through Tom Select also
  // emits a change, which re-runs the filter — harmless, and the plain text input
  // emits nothing, so restore() calls filter() itself either way.
  applyValue(value) {
    if (this.element.tomselect) {
      this.element.tomselect.setValue(value)
    } else {
      this.element.value = value
    }
  }

  get storageId() {
    return `${STORAGE_PREFIX}:${window.location.pathname}:${this.storageKeyValue}`
  }

  // Calls appropriate comparison function based on comparisonTypeValue
  // and adds or removes filter from dataset
  compare(element) {
    const filterValue  = this.element.value
    const elementValue = this.elementValue(element, this.comparisonFindPathValue)

    if (this[this.comparisonTypeValue](filterValue, elementValue)) {
      this.addFilterToDataset(element.closest('tr'), this.identifier)
    } else {
      this.removeFilterFromDataset(element.closest('tr'), this.identifier)
    }
  }

  contains(filterValue, elementValue) {
    const searchString = elementValue.replace(/\r?\n|\r/gm, '').trim().toUpperCase()
    const filterString = filterValue.toUpperCase()

    return filterString && !searchString.includes(filterString)
  }

  equals(filterValue, elementValue) {
    return filterValue && elementValue.toString() != filterValue.toString()
  }

  elementValue(element, path) {
    if (!this.allowedFindPath(path)) { return '' }

    let paths   = path.split('.')
    let current = element

    for (let i = 0; i < paths.length; ++i) {
      if (current[paths[i]] == undefined) {
        return undefined;
      } else {
        current = current[paths[i]];
      }
    }

    return current;
  }

  allowedFindPath(path) {
    const safeList = [ /dataset.*/, /checked/, /value/, /textContent/ ]

    return safeList.some(rx => rx.test(path))
  }
}
